import React, { createContext, useContext, useState, useEffect } from 'react';

const WalletContext = createContext();

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

export const WalletProvider = ({ children }) => {
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load wallet data
  const loadWalletData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      // Load balance
      const balanceResponse = await fetch('/api/wallet/balance', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (balanceResponse.ok) {
        const balanceData = await balanceResponse.json();
        setBalance(balanceData.balance_cents);
      }

      // Load recent transactions
      const transactionsResponse = await fetch('/api/wallet/transactions?limit=10', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (transactionsResponse.ok) {
        const transactionsData = await transactionsResponse.json();
        setTransactions(transactionsData);
      }
    } catch (error) {
      console.error('Error loading wallet data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Top up wallet using Payment Links
  const topUpWallet = async (amountCents) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch('/api/wallet/create-payment-link', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ amount_cents: amountCents })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create payment link');
      }

      const result = await response.json();
      
      // Redirect to Stripe Payment Link
      if (result.url) {
        window.location.href = result.url;
      } else {
        throw new Error('No payment URL received');
      }

      return result;
    } catch (error) {
      console.error('Error creating payment link:', error);
      throw error;
    }
  };

  // Refresh wallet balance (called after successful payment)
  const refreshWalletBalance = async () => {
    await loadWalletData();
  };

  // Format balance for display
  const formatBalance = (cents) => {
    return (cents / 100).toFixed(2);
  };

  // Format transaction amount
  const formatAmount = (cents) => {
    const amount = cents / 100;
    return amount >= 0 ? `+$${amount.toFixed(2)}` : `-$${Math.abs(amount).toFixed(2)}`;
  };

  // Get transaction type display
  const getTransactionTypeDisplay = (type) => {
    switch (type) {
      case 'top_up':
        return 'Wallet Top-up';
      case 'usage':
        return 'Message Usage';
      case 'refund':
        return 'Refund';
      case 'payout':
        return 'Creator Payout';
      default:
        return type;
    }
  };

  useEffect(() => {
    loadWalletData();
  }, []);

  const value = {
    balance,
    transactions,
    loading,
    loadWalletData,
    topUpWallet,
    refreshWalletBalance,
    formatBalance,
    formatAmount,
    getTransactionTypeDisplay
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};
